import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from "@mui/material";
import Cropper, { type Area, type Point } from "react-easy-crop";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.ProfilePhoto;

type Props = {
  imageSrc: string | null;
  crop: Point;
  zoom: number;
  isUploading: boolean;
  isSaveDisabled: boolean;
  onCropChange: (crop: Point) => void;
  onZoomChange: (zoom: number) => void;
  onCropComplete: (areaPixels: Area) => void;
  onCancel: () => void;
  onSave: () => void;
};

export const ProfilePhotoCropDialog = ({
  imageSrc,
  crop,
  zoom,
  isUploading,
  isSaveDisabled,
  onCropChange,
  onZoomChange,
  onCropComplete,
  onCancel,
  onSave,
}: Props) => {
  const { t } = useResource();

  return (
    <Dialog
      open={!!imageSrc}
      onClose={isUploading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t(RESOURCES.CropDialogTitle)}</DialogTitle>
      <DialogContent>
        {imageSrc && (
          <>
            <Box
              position="relative"
              width="100%"
              height={320}
              bgcolor="background.default"
              borderRadius={1}
              overflow="hidden"
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={(_, areaPixels) => onCropComplete(areaPixels)}
              />
            </Box>
            <Box mt={3} px={1}>
              <Typography fontWeight={400}>{t(RESOURCES.ZoomLabel)}</Typography>
              <Box pl={2}>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.05}
                  onChange={(_, value) => onZoomChange(value as number)}
                  aria-label={t(RESOURCES.ZoomLabel)}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={onCancel} disabled={isUploading}>
          {t(RESOURCES.CropCancel)}
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={onSave}
          loading={isUploading}
          disabled={isSaveDisabled}
        >
          {t(RESOURCES.CropSave)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
